"use client";
import { track, checkoutStarted } from '@/lib/plausible/browser';

import { useState, useEffect, useContext } from "react";
import { useRouter } from "next/navigation";
import { getPaymentService, getAvailablePaymentMethods } from "@/services/payments";
import { UserContext } from "@/context/UserContext";
import { useModal } from "@/context/ModalContext";
import config from "@/config";
import { getPaymentType, getProviderPrice } from "@/services/payments/provider-config";
import { useTranslations } from "next-intl";

const ButtonCheckout = ({ plan, isYearly = false, mode = "subscription", className, children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const paymentMethods = getAvailablePaymentMethods(plan, isYearly);
  const [error, setError] = useState("");
  const t = useTranslations("Payment");
  const router = useRouter();
  const { openModal } = useModal();
  const { user } = useContext(UserContext);


  // 开始结账转换追踪
  const gtag_report_checkout_conversion = () => {
    const callback = function () {
      // 结账转换完成后的回调
      console.log('Checkout conversion reported');
    };
    
    if (typeof gtag !== 'undefined') {
      gtag('event', 'conversion', {
        'send_to': 'AW-16635151357/xYANCJbUuvwaEP2Hofw9',
        'event_callback': callback
      });
    }
    
    return false;
  };

  const handlePayment = async (provider) => {
    if (plan.price === 0) {
      router.push("/");
      return;
    }

    if ((!user || user.is_guest)) {
      openModal("login", {
        onSuccess: () => handlePayment(provider),
        redirectTo: '/pricing'
      });
      return;
    }

    
    // 上报开始结账转换事件
    gtag_report_checkout_conversion();
    
    if (isLoading) return;
    setError("");
    setIsLoading(true);
    setShowPaymentMethods(false);
    track('CheckoutClick', { provider });

    try {
      const paymentService = getPaymentService(provider);
      
      const paymentType = getPaymentType(plan, isYearly);
      const priceId = provider === 'anyway' ? undefined : getProviderPrice(plan, provider, isYearly);

      const checkoutUrl = await paymentService.createCheckout({
        priceId,
        mode: plan.isOneTime ? 'payment' : mode,
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        user: user,
        paymentType: paymentType, // 为所有支付添加paymentType
        plan: provider === 'stripe' ? plan : undefined, // 只为Stripe传递plan对象
        isYearly: provider === 'stripe' ? isYearly : undefined // 只为Stripe传递isYearly参数
      });

      // 在当前窗口中打开结账页面，而不是新开窗口
      await checkoutStarted({ provider });
      window.location.href = checkoutUrl;
    } catch (e) {
      track('CheckoutFailed', { provider });
      setError(t("checkoutError"));
    }

    setIsLoading(false);
  };

  const handleClick = () => {
    setError("");
    if (plan.price === 0) { router.push("/"); return; }
    if (paymentMethods.length === 0) { setError(t("unavailable")); return; }
    if (paymentMethods.length === 1) {
      handlePayment(paymentMethods[0].id);
    } else if (!config.payments.enableProviderSelection) {
      const defaultMethod = paymentMethods.find(method => method.id === config.payments.defaultProvider);
      handlePayment((defaultMethod || paymentMethods[0]).id);
    } else {
      setShowPaymentMethods(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPaymentMethods && !event.target.closest('.payment-methods-container')) {
        setShowPaymentMethods(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPaymentMethods]);

  return (
    <div className="relative payment-methods-container">
      <button
        className={`group ${className}`}
        onClick={handleClick}
        disabled={isLoading}
        type="button"
        aria-expanded={showPaymentMethods}
      >
        {isLoading ? (
          <span className="loading loading-spinner loading-xs"></span>
        ) : (
          children
        )}
      </button>
      
      {error && <p role="alert" className="mt-2 text-sm text-red-400">{error}</p>}
      {showPaymentMethods && paymentMethods.length > 1 && (
        <div className="absolute top-full left-0 mt-3 w-72 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-xl shadow-lg z-50 border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              {t("chooseMethod")}
            </h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {paymentMethods.map((method) => (
              <button
                type="button"
                disabled={isLoading}
                key={method.id}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 flex items-center gap-3 group"
                onClick={() => handlePayment(method.id)}
              >
                {method.icon && (
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-600 p-2 flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-gray-500 transition-colors duration-150">
                    <img 
                      src={method.icon} 
                      alt={method.name} 
                      className="w-6 h-6 object-contain"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {method.name}
                    {method.recommended && <span className="ml-2 text-xs text-green-600">{t("recommended")}</span>}
                  </p>
                  {method.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {method.description}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ButtonCheckout;